import React, { useMemo, useState, useEffect, useCallback, useId } from 'react';
import { motion } from 'framer-motion';
import {
    AlertCircle,
    ArrowLeft,
    ArrowRight,
    Banknote,
    CircleCheck,
    Clock,
    List,
    Loader2,
    RefreshCw,
    Shield,
    Timer,
    Wallet,
    type LucideIcon,
} from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Checkbox } from 'components/ui/checkbox';
import { LogoMark } from 'components/global/LogoMark';
import { cn } from 'lib/utils';
import { formatGroupedNumber } from 'lib/dateFormat';
import { useTranslation } from 'react-i18next';
import type { StartPageConfig, StartPageFormField } from 'modules/attempt/types';
import { calculateTestPayment } from 'modules/portal-plans/testPayment';
import { PORTAL_PLAN_PICKER_PATH } from 'modules/portal-plans/paths';

export interface TestStartScreenProps {
    title: string;
    description?: string;
    totalQuestions: number;
    /** Time limit in minutes; null/0 means untimed. */
    timeLimit: number | null;
    attemptsUsed: number;
    attemptsAllowed: number | null;
    startPage: StartPageConfig;
    /** Submitting state for the live flow. */
    isStarting?: boolean;
    /** Render inside the config editor (inert, no full-screen chrome). */
    embedded?: boolean;
    error?: string | null;
    /** Whether the test requires a portal subscription to start. */
    requiresSubscription?: boolean;
    /** Whether the respondent currently holds an active subscription with remaining mocks. */
    hasSubscription?: boolean;
    /** Direct override if subscription is required and unfulfilled. */
    needsSubscription?: boolean;
    /** Action to open portal checkout dialog. */
    onBuySubscription?: () => void;
    /**
     * Starting this test charges its price (it is paid and not yet bought). The
     * screen shows the price and the balance; `onStart` still fires, and the
     * parent opens the payment step before creating the attempt.
     */
    payment?: { priceSom: number; balanceSom: number };
    /** Leaves the start page. Without it the page has no way back (e.g. inside the config editor). */
    onBack?: () => void;
    /** Status shown above the start button, such as a Payme top-up being confirmed. */
    notice?: React.ReactNode;
    /** A second card beside the start card on wide screens (below it on phones), such as an IELTS readiness check. */
    aside?: React.ReactNode;
    onStart: (identification: Record<string, string>, consentAccepted: boolean) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The three platform rules every attempt runs under (copy from the start board).
const RULES = [
    { icon: CircleCheck, title: 'autosave_active', desc: 'autosave_desc' },
    { icon: Timer, title: 'timed_session', desc: 'timer_desc' },
    { icon: Shield, title: 'secure_mode', desc: 'proctor_desc' },
] as const;

// One column per visible stat on wider screens; phones always stack them.
const STAT_COLS: Record<number, string> = {
    1: 'sm:grid-cols-1',
    2: 'sm:grid-cols-2',
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-4',
};

const SECTION_HEADING = 'text-[14.5px] font-semibold text-foreground';

function StartStat({
    icon: Icon,
    label,
    value,
    danger = false,
}: {
    icon: LucideIcon;
    label: string;
    value: React.ReactNode;
    danger?: boolean;
}) {
    return (
        <div className="flex min-w-0 flex-col gap-1.5 px-4 py-3.5 leading-tight">
            <dt className="inline-flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground/70" aria-hidden />
                {label}
            </dt>
            <dd className={cn('text-lg font-semibold tabular-nums', danger ? 'text-error-700' : 'text-foreground')}>
                {value}
            </dd>
        </div>
    );
}

export const TestStartScreen: React.FC<TestStartScreenProps> = ({
    title,
    description,
    totalQuestions,
    timeLimit,
    attemptsUsed,
    attemptsAllowed,
    startPage,
    isStarting = false,
    embedded = false,
    error,
    requiresSubscription = false,
    hasSubscription = true,
    needsSubscription,
    onBuySubscription,
    payment,
    onBack,
    notice,
    aside,
    onStart,
}) => {
    const { t } = useTranslation();
    const { instructions, consent, consentRequired, formFields, meta } = startPage;
    const idPrefix = useId();
    const consentHeadingId = `${idPrefix}-consent`;

    const isSubscriptionError =
        !!error &&
        (/subscription/i.test(error) ||
            /PORTAL_MOCK_LIMIT_REACHED/i.test(error) ||
            /PORTAL_SUBSCRIPTION_REQUIRED/i.test(error));

    const computedNeedsSubscription =
        needsSubscription ??
        (isSubscriptionError || (requiresSubscription && !hasSubscription));

    const handleBuySubscription = useCallback(() => {
        if (onBuySubscription) {
            onBuySubscription();
        } else {
            window.location.href = PORTAL_PLAN_PICKER_PATH;
        }
    }, [onBuySubscription]);

    const formatDuration = (minutes: number): string => {
        if (minutes < 60) return `${minutes} ${t('min')}`;
        const hrs = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins ? `${hrs} ${t('hour_short')} ${mins} ${t('min')}` : `${hrs} ${t('hour_short')}`;
    };

    const [values, setValues] = useState<Record<string, string>>({});
    const [consentChecked, setConsentChecked] = useState(false);
    const [touched, setTouched] = useState(false);

    const orderedFields = useMemo(
        () => [...formFields].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
        [formFields],
    );

    const fieldError = (field: StartPageFormField): string | null => {
        const raw = (values[field.id] ?? '').trim();
        if (field.mandatory && !raw) return t('this_field_is_required');
        if (raw && field.type === 'email' && !EMAIL_RE.test(raw)) return t('enter_a_valid_email');
        return null;
    };

    const hasFieldErrors = orderedFields.some((f) => fieldError(f) !== null);
    const consentBlocked = Boolean(consent) && consentRequired && !consentChecked;
    const attemptsLeft =
        attemptsAllowed !== null ? Math.max(0, attemptsAllowed - attemptsUsed) : null;
    const outOfAttempts = attemptsLeft !== null && attemptsLeft <= 0;

    const canStart = !hasFieldErrors && !consentBlocked && !outOfAttempts;

    const handleStart = useCallback(() => {
        setTouched(true);
        if (!canStart || isStarting) return;
        const identification: Record<string, string> = {};
        orderedFields.forEach((f) => {
            const v = (values[f.id] ?? '').trim();
            if (v) identification[f.id] = v;
        });
        onStart(identification, consentChecked);
    }, [canStart, isStarting, orderedFields, values, onStart, consentChecked]);

    // Keyboard support: Press Enter to submit if onStart is ready or trigger buy subscription
    useEffect(() => {
        if (embedded) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
                const target = e.target as HTMLElement | null;
                if (target?.tagName === 'TEXTAREA') return;
                // A dialog over the page (the payment step) keeps Enter for its own buttons.
                if (target?.closest?.('[role="dialog"]')) return;
                if (computedNeedsSubscription) {
                    e.preventDefault();
                    handleBuySubscription();
                } else if (canStart && !isStarting) {
                    e.preventDefault();
                    handleStart();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [embedded, canStart, isStarting, handleStart, computedNeedsSubscription, handleBuySubscription]);

    const showQuestions = meta.showQuestionCount !== false && totalQuestions > 0;
    const showDuration = meta.showEstimatedDuration !== false && !!timeLimit;
    const showAttempts = meta.showAttemptsRemaining !== false && attemptsLeft !== null;
    const statCount = [showQuestions, showDuration, showAttempts, Boolean(payment)].filter(Boolean).length;

    const som = (value: number) => `${formatGroupedNumber(value)} ${t('portal_tests_currency')}`;
    const breakdown = payment ? calculateTestPayment(payment.priceSom, payment.balanceSom) : null;
    const balanceShort = Boolean(breakdown && breakdown.dueSom > 0);
    const startLabel = !breakdown
        ? t('start_assessment')
        : balanceShort
          ? t('top_up_and_start')
          : t('pay_and_start', { price: som(breakdown.priceSom) });

    const backButton =
        onBack && !embedded ? (
            <Button type="button" variant="ghost" onClick={onBack} className="ml-auto gap-1.5 text-muted-foreground">
                <ArrowLeft className="h-4 w-4" aria-hidden />
                {t('back_to_tests')}
            </Button>
        ) : null;

    const card = (
        <article
            className={cn(
                'text-foreground',
                embedded
                    ? 'rounded-lg border border-border bg-card px-6 py-6 sm:px-9 sm:py-8'
                    : // Phones: full-bleed white page, no card (Exam-Start-Phone).
                      'px-4 pb-6 pt-5 sm:rounded-lg sm:border sm:border-border sm:bg-card sm:px-9 sm:py-8 sm:shadow-sm',
            )}
        >
            {/* The org · group line on the board has no source in the start-info payload. */}
            <h1 className="text-[22px] font-bold leading-[1.25] tracking-[-0.02em] sm:text-[26px]">{title}</h1>
            {description && (
                <p className="mt-2 text-[15px] leading-[1.55] text-muted-foreground">{description}</p>
            )}

            {statCount > 0 && (
                <dl
                    className={cn(
                        'mt-5 grid grid-cols-1 divide-y divide-border rounded-md border border-border sm:divide-x sm:divide-y-0',
                        STAT_COLS[statCount],
                    )}
                >
                    {showQuestions && <StartStat icon={List} label={t('questions')} value={totalQuestions} />}
                    {showDuration && (
                        <StartStat icon={Clock} label={t('duration')} value={formatDuration(timeLimit as number)} />
                    )}
                    {showAttempts && (
                        <StartStat
                            icon={RefreshCw}
                            label={t('attempts_remaining')}
                            value={attemptsAllowed !== null ? `${attemptsLeft} / ${attemptsAllowed}` : String(attemptsLeft)}
                            danger={outOfAttempts}
                        />
                    )}
                    {breakdown && (
                        <StartStat icon={Banknote} label={t('paid_test_price')} value={som(breakdown.priceSom)} />
                    )}
                </dl>
            )}

            {/* Author instructions, when the start page has them */}
            {instructions && (
                <>
                    <h2 className={cn(SECTION_HEADING, 'mb-2 mt-6')}>{t('instructions')}</h2>
                    <p className="whitespace-pre-wrap rounded-md border border-border bg-background px-4 py-3.5 text-[14.5px] leading-[1.6] text-foreground/80">
                        {instructions}
                    </p>
                </>
            )}

            {/* Platform rules */}
            <h2 className={cn(SECTION_HEADING, 'mt-6')}>{t('test_guidelines')}</h2>
            <ul className="mt-1 divide-y divide-border">
                {RULES.map(({ icon: Icon, title: ruleTitle, desc }) => (
                    <li key={ruleTitle} className="flex gap-3 py-3">
                        <Icon className="mt-px h-[18px] w-[18px] shrink-0 text-foreground/80" aria-hidden />
                        <span className="flex min-w-0 flex-col gap-0.5 leading-tight">
                            <span className="text-[14.5px] font-semibold">{t(ruleTitle)}</span>
                            <span className="text-sm text-muted-foreground">{t(desc)}</span>
                        </span>
                    </li>
                ))}
            </ul>

            {/* Identification fields */}
            {orderedFields.length > 0 && (
                <>
                    <h2 className={cn(SECTION_HEADING, 'mb-3 mt-5')}>{t('your_details')}</h2>
                    <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                        {orderedFields.map((field) => {
                            const err = touched ? fieldError(field) : null;
                            const inputId = `sp-${field.id}`;
                            return (
                                <div key={field.id} className="flex flex-col gap-1.5">
                                    <label htmlFor={inputId} className="text-[13.5px] font-medium text-foreground/80">
                                        {field.label}
                                        {field.mandatory && <span className="ml-0.5 text-error-600">*</span>}
                                    </label>
                                    <Input
                                        id={inputId}
                                        type={
                                            field.type === 'email'
                                                ? 'email'
                                                : field.type === 'number'
                                                  ? 'number'
                                                  : 'text'
                                        }
                                        value={values[field.id] ?? ''}
                                        onChange={(e) =>
                                            setValues((prev) => ({
                                                ...prev,
                                                [field.id]: e.target.value,
                                            }))
                                        }
                                        aria-invalid={!!err}
                                        aria-describedby={err ? `${inputId}-error` : undefined}
                                        className="h-11 text-[15px] md:text-[15px]"
                                    />
                                    {err && (
                                        <p id={`${inputId}-error`} className="text-[12.5px] text-error-700">
                                            {err}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Consent */}
            {consent && (
                <>
                    <h2 id={consentHeadingId} className={cn(SECTION_HEADING, 'mb-2 mt-5')}>
                        {t('exam_consent')}
                    </h2>
                    {/* Scrolls when long, so it takes keyboard focus. */}
                    <div
                        role="region"
                        aria-labelledby={consentHeadingId}
                        tabIndex={0}
                        className="max-h-[110px] overflow-y-auto whitespace-pre-wrap rounded-md border border-border px-3.5 py-3 text-[13.5px] leading-[1.55] text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                        {consent}
                    </div>
                    <label className="mt-3 flex cursor-pointer items-center gap-2.5 text-[14.5px]">
                        <Checkbox
                            checked={consentChecked}
                            onCheckedChange={(c) => setConsentChecked(c === true)}
                            className="h-[18px] w-[18px]"
                        />
                        <span>
                            {t('i_have_read_and_agree_to_the_above')}
                            {consentRequired && <span className="ml-1 text-error-600">*</span>}
                        </span>
                    </label>
                </>
            )}

            {notice && <div className="mt-6">{notice}</div>}

            {/* Subscription / error notice */}
            {computedNeedsSubscription ? (
                <div className="mt-6 flex gap-3 rounded-md border border-border bg-background p-4">
                    <AlertCircle className="mt-px h-[18px] w-[18px] shrink-0 text-warning-600" aria-hidden />
                    <div className="flex min-w-0 flex-col gap-1">
                        <p className="text-[14.5px] font-semibold">
                            {t('subscription_required_for_test', 'Obuna talab qilinadi')}
                        </p>
                        <p className="text-sm leading-relaxed text-muted-foreground">
                            {error ||
                                t(
                                    'subscription_required_for_test_desc',
                                    'Ushbu testdan foydalanish uchun faol portal obunasi va mavjud urinishlar talab qilinadi.',
                                )}
                        </p>
                    </div>
                </div>
            ) : (
                (error || outOfAttempts) && (
                    <div
                        role="alert"
                        className="mt-6 flex items-start gap-2.5 rounded-md border border-error-200 bg-error-50 px-3 py-2.5 text-sm text-error-700 dark:border-error-900/60 dark:bg-error-900/30 dark:text-error-400"
                    >
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                        <p>{error || t('you_have_no_attempts_remaining_for_this_test')}</p>
                    </div>
                )
            )}

            {/* Primary action */}
            <div className="mt-6">
                {breakdown && !computedNeedsSubscription && !balanceShort && (
                    <div
                        role="status"
                        className="mb-3 flex items-start gap-2.5 rounded-md border border-success-200 bg-success-50 px-3.5 py-3 text-success-700 dark:border-success-900/60 dark:bg-success-900/30 dark:text-success-400"
                    >
                        <Wallet className="mt-0.5 h-[18px] w-[18px] shrink-0" aria-hidden />
                        <p className="flex min-w-0 flex-col gap-0.5 leading-snug">
                            <span className="text-[14.5px] font-semibold">
                                {t('paid_test_balance_ready', { balance: som(payment?.balanceSom ?? 0) })}
                            </span>
                            <span className="text-[13.5px]">
                                {t('paid_test_from_balance', { price: som(breakdown.priceSom) })}
                            </span>
                        </p>
                    </div>
                )}
                {breakdown && !computedNeedsSubscription && balanceShort && (
                    <p className="mb-3 flex items-start gap-2 text-[13.5px] leading-snug text-muted-foreground">
                        <Wallet className="mt-px h-4 w-4 shrink-0 text-muted-foreground/70" aria-hidden />
                        <span>
                            {t('paid_test_top_up_hint', {
                                balance: som(payment?.balanceSom ?? 0),
                                amount: som(breakdown.dueSom),
                            })}
                        </span>
                    </p>
                )}
                {computedNeedsSubscription ? (
                    <Button
                        type="button"
                        size="lg"
                        className="h-12 w-full gap-2 text-[15px] font-semibold"
                        onClick={handleBuySubscription}
                    >
                        {t('buy_subscription', 'Obuna sotib olish')}
                        <ArrowRight className="h-4 w-4" aria-hidden />
                    </Button>
                ) : (
                    <Button
                        type="button"
                        size="lg"
                        className="h-12 w-full gap-2 text-[15px] font-semibold"
                        onClick={handleStart}
                        disabled={isStarting || (touched && !canStart) || outOfAttempts}
                    >
                        {isStarting ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                {t('starting')}
                            </>
                        ) : (
                            <>
                                {startLabel}
                                <ArrowRight className="h-4 w-4" aria-hidden />
                            </>
                        )}
                    </Button>
                )}

                <p className="mt-3 flex items-start justify-center gap-1.5 text-[12.5px] text-muted-foreground">
                    <CircleCheck
                        className={cn(
                            'mt-[3px] h-3.5 w-3.5 shrink-0',
                            computedNeedsSubscription ? 'text-muted-foreground' : 'text-success-600',
                        )}
                        aria-hidden
                    />
                    {computedNeedsSubscription
                        ? t('subscription_unlocks_all', 'Obuna orqali barcha premium testlarga kirish ochiladi')
                        : t('environment_ready')}
                </p>
            </div>
        </article>
    );

    if (embedded) {
        return (
            <div data-surface="exam" className="flex justify-center">
                <div className="w-full max-w-xl">{card}</div>
            </div>
        );
    }

    return (
        // Inside the Studio phone shell the page is full-bleed: cancel the shell's in-flow padding.
        <div
            data-surface="exam"
            className="min-h-screen bg-card text-foreground [[data-mobile-studio]_&]:-mx-4 [[data-mobile-studio]_&]:-mt-3 sm:bg-[#F4F5F7] sm:px-6 sm:pb-12 sm:pt-12 sm:dark:bg-background"
        >
            <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: 'easeOut' }}
            >
                {/* Phone: a 56px logo bar across the top */}
                <header className="flex h-14 items-center gap-[9px] border-b border-border px-4 sm:hidden">
                    <LogoMark size={24} className="rounded-sm" />
                    <span aria-hidden className="font-display text-sm font-extrabold tracking-[-0.02em]">
                        {t('examing')}
                    </span>
                    {backButton}
                </header>
                <div className={cn('mx-auto w-full', aside ? 'max-w-[1100px]' : 'max-w-[672px]')}>
                    {/* Wider screens: the brand sits above the card */}
                    <header className="mb-4 hidden items-center gap-2.5 sm:flex">
                        <LogoMark size={26} className="rounded-sm" />
                        <span aria-hidden className="font-display text-sm font-extrabold tracking-[-0.02em]">
                            {t('examing')}
                        </span>
                        {backButton}
                    </header>
                    {aside ? (
                        <div className="grid gap-5 lg:grid-cols-[1.25fr_1fr] lg:items-start">
                            {card}
                            <div className="px-4 pb-6 sm:px-0 sm:pb-0">{aside}</div>
                        </div>
                    ) : (
                        card
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default TestStartScreen;
